import { createClient } from "@supabase/supabase-js";

/**
 * Supabase client configuration
 *
 * Environment variables:
 * - SUPABASE_URL: Your Supabase project URL (e.g., https://xxx.supabase.co)
 * - SUPABASE_ANON_KEY: Your Supabase anonymous/public key
 *
 * These are set automatically by setup-db.ts when you create a Supabase project.
 *
 * For local development, create a .env file with:
 *   SUPABASE_URL=https://your-project.supabase.co
 *   SUPABASE_ANON_KEY=your-anon-key
 */

// Get environment variables
// These placeholders are replaced at build time via Bun.build's define option (see build.ts)
// Using global declarations to avoid issues with process.env replacement
declare const __SUPABASE_URL__: string;
declare const __SUPABASE_ANON_KEY__: string;

// Safely access process.env (only exists on server/build time, not in browser)
const getEnvVar = (key: string): string => {
  try {
    // This will be replaced at build time, but may error in browser during dev
    if (typeof process !== "undefined" && process.env) {
      return (process.env as Record<string, string | undefined>)[key] || "";
    }
  } catch {
    // process is not defined in browser
  }
  return "";
};

// Read from injected globals (production), with fallback for dev mode
const supabaseUrl =
  typeof __SUPABASE_URL__ !== "undefined" && __SUPABASE_URL__
    ? __SUPABASE_URL__
    : getEnvVar("SUPABASE_URL");
const supabaseAnonKey =
  typeof __SUPABASE_ANON_KEY__ !== "undefined" && __SUPABASE_ANON_KEY__
    ? __SUPABASE_ANON_KEY__
    : getEnvVar("SUPABASE_ANON_KEY");

// Placeholder URL for when credentials aren't configured
// This prevents the app from crashing but auth features won't work
const PLACEHOLDER_URL = "https://placeholder.supabase.co";
const PLACEHOLDER_KEY = "placeholder-key";

const finalUrl = supabaseUrl || PLACEHOLDER_URL;
const finalKey = supabaseAnonKey || PLACEHOLDER_KEY;

// Log warning in browser if not configured
const isConfigured = supabaseUrl && supabaseAnonKey;
if (typeof window !== "undefined" && !isConfigured) {
  console.warn(
    "[Auth] Supabase not configured. Run 'bun run setup:db' and restart the dev server to enable authentication."
  );
}

export const supabase = createClient(finalUrl, finalKey, {
  auth: {
    // Persist session to localStorage by default
    persistSession: true,
    // Auto-refresh the session before it expires
    autoRefreshToken: true,
    // Detect session from URL (for OAuth redirects)
    detectSessionInUrl: true,
  },
});

/**
 * Whether Supabase is properly configured with credentials.
 * If false, auth features will not work.
 */
export const isSupabaseConfigured = isConfigured;

export default supabase;
